import { Analytics } from '@cobliteam/analytics';
import type { PathAssociation } from '@cobliteam/api-lib/src/dashboard/pathAssociation/Types';
import { Location } from '@cobliteam/common-types';
import Icon from '@cobliteam/ui/icons';
import DataTable, { DataTableAction, DataTableCell, DataTableCellVehicle } from '@cobliteam/ui-business/components/DataTable';
import { formatDurationShort } from '@cobliteam/utils/time';
import type { ColumnDef } from '@tanstack/react-table';
import queryString, { type ParseOptions } from 'query-string';
import { Link } from 'react-router-dom';

const columnsDef: ColumnDef<PathAssociation>[] = [
  {
    id: 'vehicle',
    accessorKey: 'vehicle',
    accessorFn: (row) => `${row.vehicle?.licensePlate} - ${row.vehicle?.model} - ${row.vehicle?.brand}`,
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => <DataTableCellVehicle brand={row.original.vehicle.brand} model={row.original.vehicle.model} licensePlate={row.original.vehicle.licensePlate} />,
    meta: {
      name: 'Veículo',
    },
  },
  {
    id: 'periodPaths',
    accessorKey: 'periodPaths',
    accessorFn: (row) => row.routeSteps?.length ?? 0,
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => <DataTableCell content={row.original.routeSteps?.length ?? 0} />,
    meta: {
      name: 'Trechos no período',
    },
  },
  {
    id: 'associatedPaths',
    accessorKey: 'associatedPaths',
    accessorFn: (row) => row.routeSteps?.filter((step) => step.driverId).length ?? 0,
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => {
      const routeSteps = row.original.routeSteps ?? [];
      const associatedCount = routeSteps.filter((step) => step.driverId).length;
      return <DataTableCell content={`${associatedCount}/${routeSteps.length}`} />;
    },
    meta: {
      name: 'Trechos associados',
    },
  },
  {
    id: 'duration',
    accessorKey: 'duration',
    accessorFn: (row) => row.routeSteps?.reduce((acc, step) => acc + (step.endTime - step.startTime), 0) ?? 0,
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => {
      const routeSteps = row.original.routeSteps;
      if (!routeSteps || routeSteps.length === 0) {
        return <DataTableCell content='--' />;
      }

      const totalDuration = routeSteps.reduce((acc, step) => acc + (step.endTime - step.startTime), 0);
      const associatedDuration = routeSteps.filter((step) => step.driverId).reduce((acc, step) => acc + (step.endTime - step.startTime), 0);

      return <DataTableCell title={formatDurationShort(associatedDuration)} subtitle={`Total: ${formatDurationShort(totalDuration)}`} />;
    },
    meta: {
      name: 'Duração associada',
    },
  },
  {
    id: 'actions',
    header: DataTable.Header,
    cell: ({ row }) => {
      const parseOptions: ParseOptions = { arrayFormat: 'comma' };
      const parsedQuery = queryString.parse(location.search, parseOptions);

      const handleClick = () => {
        Analytics.track('Path Association - Table - See Paths');
      };

      return (
        <Link
          to={{
            pathname: `${Location.PATH_ASSOCIATION}/vehicle/${row.original.vehicle?.id}`,
            search: queryString.stringify({ startDate: parsedQuery.startDate, endDate: parsedQuery.endDate }),
          }}
        >
          <DataTableAction icon={<Icon name='eye' />} tooltip='Ver trechos' onClick={handleClick} />
        </Link>
      );
    },
    meta: {
      name: 'Ações',
      alignHeader: 'center',
    },
  },
];

export default columnsDef;
